import { clinicNow, clinicToday } from '../../utils/clinicTime';

// What the two attendance nudges share: PunchInReminder at the start of the day and
// PunchOutReminder at the end. Both compare a schedule's "HH:mm" against the hospital's wall clock
// and both remember what the person did about it for the rest of today, and both getting that
// arithmetic right separately is how one of them quietly drifts from the other.

/** Minutes since midnight, in the hospital's own timezone rather than the browser's. */
export const clinicMinutes = () => {
  const now = clinicNow();
  return now.getHours() * 60 + now.getMinutes();
};

/** "16:45" to minutes since midnight, or null when there is no usable time. */
export const minutesOf = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/** "16:45" as a person reads a clock: "4:45 PM". */
export const asClockTime = (hhmm) => {
  const total = minutesOf(hhmm);
  if (total == null) return '';
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

/**
 * Remembered per person per day, so a reload does not nag again and tomorrow starts clean.
 *
 * <p>Every read and write is guarded: in a private window, or with site data blocked, the accessor
 * itself throws. A nudge that cannot remember is still a working nudge for this page's lifetime,
 * which is much better than a blank screen.
 */
export const reminderStore = (prefix) => ({
  read(userId) {
    try {
      return JSON.parse(localStorage.getItem(`${prefix}_${userId || 'me'}_${clinicToday()}`) || 'null') || {};
    } catch {
      return {};
    }
  },
  write(userId, value) {
    try {
      localStorage.setItem(`${prefix}_${userId || 'me'}_${clinicToday()}`, JSON.stringify(value));
    } catch {
      /* Nothing to do. */
    }
  },
});

/** getDay() order, so today's slot can be looked up in a schedule's day map. */
export const DAY_KEYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/** Today's slot ({ from, to }) out of the day map a schedule carries, or undefined. */
export const todaySlot = (slots) => slots?.[DAY_KEYS[clinicNow().getDay()]];
